import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SectionCard from './SectionCard';
import DocumentViewer from './DocumentViewer';
import FrontDeskSMS from './FrontDeskSMS';
import { FOLDER_IDS, EXTERNAL_URLS } from '../config';
import './Portal.css';

// Section configuration - maps keys to display properties
const SECTIONS = {
  inventory: {
    key: 'inventory',
    name: 'Inventory',
    icon: '📦',
    description: 'Par levels, supplier directory, SOPs',
    folderId: FOLDER_IDS.inventory
  },
  finance: {
    key: 'finance',
    name: 'Finance',
    icon: '💰',
    description: 'P&L, commission model, budget, COA',
    folderId: FOLDER_IDS.finance
  },
  frontdesk: {
    key: 'frontdesk',
    name: 'Front Desk',
    icon: '🛎️',
    description: 'Booking procedures, guest standards',
    folderId: FOLDER_IDS.frontdesk,
    hasSubpage: true
  },
  boh: {
    key: 'boh',
    name: 'Back of House',
    icon: '👨‍🍳',
    description: 'Zone maps, par levels, reorder tracker',
    folderId: FOLDER_IDS.boh
  },
  hr: {
    key: 'hr',
    name: 'HR & Onboarding',
    icon: '👥',
    description: 'New hire docs, orientation, handbook',
    folderId: FOLDER_IDS.hr
  },
  marketing: {
    key: 'marketing',
    name: 'Marketing',
    icon: '📣',
    description: 'Campaign system, email templates, SEO',
    folderId: FOLDER_IDS.marketing
  },
  technicians: {
    key: 'technicians',
    name: 'Technicians',
    icon: '💅',
    description: 'Roster, job codes, certifications',
    folderId: FOLDER_IDS.technicians,
    externalUrl: EXTERNAL_URLS.technicians
  }
};

function Portal() {
  const { user, access, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [view, setView] = useState('dashboard'); // 'dashboard', 'frontdesk-sms'

  // Get allowed sections for current user
  const allowedSections = access?.sections || [];
  const userRole = access?.role || 'member';
  const userName = user?.displayName || user?.email?.split('@')[0] || 'User';

  const handleSectionClick = (sectionKey) => {
    const section = SECTIONS[sectionKey];
    if (section?.externalUrl) {
      // Open external URL for Technicians section
      window.open(section.externalUrl, '_blank');
    } else if (sectionKey === 'frontdesk' && section?.hasSubpage) {
      // Navigate to Front Desk SMS view
      setView('frontdesk-sms');
      setActiveTab('frontdesk');
    } else if (section?.folderId) {
      // TODO: Show document list for this section
      console.log('Opening section:', sectionKey);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCloseDoc = () => {
    setSelectedDoc(null);
  };

  const handleTabClick = (tabKey) => {
    setActiveTab(tabKey);
    if (tabKey === 'dashboard') {
      setView('dashboard');
    } else if (tabKey === 'frontdesk') {
      // If clicking Front Desk tab, go to SMS view
      setView('frontdesk-sms');
    } else if (SECTIONS[tabKey]) {
      handleSectionClick(tabKey);
    }
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
    setActiveTab('dashboard');
  };

  // Front Desk SMS view
  if (view === 'frontdesk-sms' && activeTab === 'frontdesk') {
    return (
      <div className="portal-container">
        <header className="portal-header">
          <div className="header-top">
            <div className="header-left">
              <h1 className="portal-logo">GLOSS <em>Hub</em></h1>
            </div>
            <div className="header-right">
              {access?.role === 'owner' || access?.role === 'co-owner' ? (
                <button className="admin-btn" onClick={() => navigate('/admin')}>
                  Admin
                </button>
              ) : null}
              <div className="user-info">
                <img 
                  src={user?.photoURL || '/default-avatar.png'} 
                  alt={userName}
                  className="user-avatar"
                />
                <div className="user-details">
                  <span className="user-name">{userName}</span>
                  <span className="user-role">{userRole}</span>
                </div>
              </div>
              <button className="logout-btn" onClick={handleLogout}>
                Sign Out
              </button>
            </div>
          </div>
          <nav className="portal-nav">
            <button
              className={`portal-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={handleBackToDashboard}
            >
              Dashboard
            </button>
            {allowedSections.map(key => (
              <button
                key={key}
                className={`portal-tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => handleTabClick(key)}
              >
                {SECTIONS[key]?.name || key}
              </button>
            ))}
          </nav>
        </header>
        
        <div className="portal-content-full">
          <FrontDeskSMS />
        </div>
      </div>
    );
  }

  if (selectedDoc) {
    return (
      <DocumentViewer 
        doc={selectedDoc} 
        onClose={handleCloseDoc}
      />
    );
  }

  // Build nav items from allowed sections (no icons)
  const navItems = [
    { key: 'dashboard', label: 'Dashboard' },
    ...allowedSections.map(key => ({
      key,
      label: SECTIONS[key]?.name || key
    }))
  ];

  return (
    <div className="portal-container">
      <header className="portal-header">
        <div className="header-top">
          <div className="header-left">
            <h1 className="portal-logo">GLOSS <em>Hub</em></h1>
          </div>
          <div className="header-right">
            {access?.role === 'owner' || access?.role === 'co-owner' ? (
              <button className="admin-btn" onClick={() => navigate('/admin')}>
                Admin
              </button>
            ) : null}
            <div className="user-info">
              <img 
                src={user?.photoURL || '/default-avatar.png'} 
                alt={userName}
                className="user-avatar"
              />
              <div className="user-details">
                <span className="user-name">{userName}</span>
                <span className="user-role">{userRole}</span>
              </div>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              Sign Out
            </button>
          </div>
        </div>
        <nav className="portal-nav">
          {navItems.map(item => (
            <button
              key={item.key}
              className={`portal-tab ${activeTab === item.key ? 'active' : ''}`}
              onClick={() => handleTabClick(item.key)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="portal-main">
        <div className="welcome-section">
          <h2>Welcome back, <em>{userName.split(' ')[0]}</em></h2>
          <p>Select a section below to access your documents and resources.</p>
        </div>

        <div className="sections-grid">
          {allowedSections.map(sectionKey => {
            const section = SECTIONS[sectionKey];
            if (!section) return null;
            return (
              <SectionCard
                key={sectionKey}
                section={section}
                onClick={() => handleSectionClick(sectionKey)}
              />
            );
          })}
        </div>

        {allowedSections.length === 0 && (
          <div className="no-access">
            <p>You don't have access to any sections yet.</p>
            <p>Contact your administrator to request access.</p>
          </div>
        )}
      </main>

      <footer className="portal-footer">
        <p>© 2026 Gloss Nail Bar • Confidential</p>
      </footer>
    </div>
  );
}

export default Portal;
export { SECTIONS };
